import { uniq } from "lodash-es";
import type {
  EstateAgentDetail,
  EstateAgentMcpService,
} from "@/api/temp/AgentEstateService";
import type {
  AgentProtection,
  AgentResourceLink,
  GatewayPath,
  ProtectionDataSource,
  ProtectionMcpService,
  RegisteredDataSource,
  RegisteredMcpService,
} from "@/api/temp/AgentProtectionService";

// Access & Protection's record from agent details alone, shared by the demo
// store and the live adapter, with no API dependency.

/** The estate's side of one agent, as Access & Protection reads it. */
export interface EstateView {
  name: string;
  mcp: RegisteredMcpService[];
  sources: RegisteredDataSource[];
  links: AgentResourceLink[];
  gatewayPaths: GatewayPath[];
}

const nativeSupported = (platform: string) =>
  platform === "Snowflake" || platform === "Databricks";

export const toRegistered = (
  service: Pick<
    EstateAgentMcpService,
    "id" | "name" | "technology" | "capabilities" | "registryServerId"
  > & {
    tools: Array<
      Pick<
        EstateAgentMcpService["tools"][number],
        "id" | "name" | "description" | "classification" | "risk"
      > &
        Partial<Pick<EstateAgentMcpService["tools"][number], "dataSourceId">>
    >;
  },
): RegisteredMcpService => ({
  id: service.id,
  name: service.name,
  technology: service.technology,
  platformLabel: service.technology,
  description: service.capabilities.join(", "),
  tools: service.tools.map(tool => ({
    id: tool.id,
    name: tool.name,
    description: tool.description,
    classification: tool.classification,
    risk: tool.risk,
    dataSourceId: tool.dataSourceId,
  })),
  dataSourceIds: uniq(
    service.tools.flatMap(tool =>
      tool.dataSourceId ? [tool.dataSourceId] : [],
    ),
  ),
  registryServerId: service.registryServerId,
});

/** A link's identity: one link per resource kind and id. */
export const linkKey = (
  link: Pick<AgentResourceLink, "resourceKind" | "resourceId">,
) => `${link.resourceKind}:${link.resourceId}`;

/** What the live adapter knows beyond agent details. */
export interface LiveProjectionContext {
  /**
   * The MCP servers this agent has a binding to. A bound service was added
   * here, so it can be removed here - the detail model carries no
   * `connectionOrigin`, which left every live service reading DISCOVERED.
   */
  boundMcpServerIds?: ReadonlySet<string>;
  /** Tool models carry no data source: say unknown, never zero. */
  dataSourcesUnknown?: boolean;
}

/** The estate's side of an agent, derived on every read. */
export const fromEstate = (
  agent: EstateAgentDetail,
  context: LiveProjectionContext = {},
): EstateView => {
  const services = agent.mcpServices;
  const mcp = services.map(service => ({
    ...toRegistered(service),
    ...(context.dataSourcesUnknown ? { dataSourcesUnknown: true } : {}),
  }));
  const isManual = (service: EstateAgentMcpService) =>
    service.connectionOrigin === "MANUAL" ||
    !!context.boundMcpServerIds?.has(service.id);
  const sources = new Map<string, RegisteredDataSource>();
  services.forEach(service =>
    service.tools.forEach(tool => {
      if (!tool.dataSourceId || sources.has(tool.dataSourceId)) return;
      const platform = tool.dataSourcePlatform ?? "Data platform";
      sources.set(tool.dataSourceId, {
        id: tool.dataSourceId,
        name: tool.dataSourceName ?? tool.dataSourceId,
        platform,
        platformId: tool.dataSourcePlatformId ?? platform.toLowerCase(),
        environment: "Production",
        nativePolicySupported: nativeSupported(platform),
      });
    }),
  );
  // Setup follows the estate's readiness; an unreadable status stays unknown.
  // A manual connection is one an administrator configured.
  const readiness = agent.enforcementReadiness?.status;
  const discoveredSetup: GatewayPath["setup"] =
    readiness === "SETUP_REQUIRED"
      ? "NOT_CONFIGURED"
      : readiness === "READY" || readiness === "ATTENTION_REQUIRED"
        ? "CONFIGURED"
        : "UNKNOWN";
  return {
    name: agent.name,
    mcp,
    sources: [...sources.values()],
    links: [
      ...services.map(
        (service): AgentResourceLink => ({
          resourceKind: "MCP_SERVICE",
          resourceId: service.id,
          origin: isManual(service) ? "MANUAL" : "DISCOVERED",
          verification: "NOT_VERIFIED",
          addedAt: service.connectedAt,
          addedBy: service.connectedBy,
        }),
      ),
      ...[...sources.values()].map(
        (source): AgentResourceLink => ({
          resourceKind: "DATA_SOURCE",
          resourceId: source.id,
          origin: "DISCOVERED",
          verification: "NOT_VERIFIED",
          accessPath: "THROUGH_MCP",
          linkedMcpServiceIds: mcp
            .filter(service => service.dataSourceIds.includes(source.id))
            .map(service => service.id),
        }),
      ),
    ],
    gatewayPaths: services.map(service => ({
      mcpServiceId: service.id,
      setup: isManual(service) ? "CONFIGURED" : discoveredSetup,
      verification: "PENDING",
    })),
  };
};

/** What a record holds beyond the estate: the demo store's own state. */
export type ProtectionOwnState = Pick<
  AgentProtection,
  | "seeded"
  | "nativePolicies"
  | "mcpPolicies"
  | "mcpRules"
  | "expectations"
  | "assessment"
  | "lastRun"
  | "scenario"
>;

/** An agent nothing has been recorded for: not assessed, nothing authored. */
export const emptyOwnState = (): ProtectionOwnState => ({
  seeded: false,
  nativePolicies: [],
  mcpPolicies: [],
  mcpRules: [],
  expectations: [],
  assessment: {
    state: "NOT_EVALUATED",
    findings: [],
    evaluatedDataSourceIds: [],
  },
  scenario: { failNextAssociation: false },
});

const byId = <T extends { id: string }>(items: T[]) =>
  new Map(items.map(item => [item.id, item]));

/**
 * The resolved record: links joined to the registered services and sources
 * they name. The one join behind the demo store and the live adapter, so the
 * demo stays a faithful preview of live.
 */
export const joinProtection = ({
  agentId,
  agentName,
  links,
  mcpById,
  sourceById,
  gatewayPaths,
  own,
}: {
  agentId: string;
  agentName: string;
  links: AgentResourceLink[];
  mcpById: Map<string, RegisteredMcpService>;
  sourceById: Map<string, RegisteredDataSource>;
  gatewayPaths: GatewayPath[];
  own: ProtectionOwnState;
}): AgentProtection => {
  const mcpServices: ProtectionMcpService[] = links
    .filter(link => link.resourceKind === "MCP_SERVICE")
    .flatMap(link => {
      const service = mcpById.get(link.resourceId);
      return service ? [{ ...service, link }] : [];
    });
  const mcpIds = new Set(mcpServices.map(service => service.id));
  const dataSources: ProtectionDataSource[] = links
    .filter(link => link.resourceKind === "DATA_SOURCE")
    .flatMap(link => {
      const source = sourceById.get(link.resourceId);
      if (!source) return [];
      const recorded = mcpServices
        .filter(service => service.dataSourceIds.includes(source.id))
        .map(service => service.id);
      const asserted =
        link.accessPath === "THROUGH_MCP"
          ? (link.linkedMcpServiceIds ?? [])
          : [];
      return [
        {
          ...source,
          link,
          viaMcpServiceIds: uniq([...recorded, ...asserted]).filter(id =>
            mcpIds.has(id),
          ),
        },
      ];
    });
  // A rule on a service no longer associated no longer applies here.
  const mcpRules = own.mcpRules.filter(
    rule => !rule.mcpServiceId || mcpIds.has(rule.mcpServiceId),
  );
  // Field by field: the demo store passes its whole state as `own`.
  return {
    agentId,
    agentName,
    seeded: own.seeded,
    mcpServices,
    dataSources,
    nativePolicies: own.nativePolicies,
    mcpPolicies: own.mcpPolicies.filter(policy =>
      mcpRules.some(rule => rule.policyId === policy.id),
    ),
    mcpRules,
    expectations: own.expectations,
    assessment: own.assessment,
    gatewayPaths: gatewayPaths.filter(path => mcpIds.has(path.mcpServiceId)),
    lastRun: own.lastRun,
    scenario: own.scenario,
  };
};

/**
 * An agent's protection record from its details alone - what the demo store
 * returns for an agent it holds nothing for.
 */
export const projectProtection = (
  agent: EstateAgentDetail,
  context?: LiveProjectionContext,
): AgentProtection => {
  const estate = fromEstate(agent, context);
  return joinProtection({
    agentId: agent.id,
    agentName: estate.name,
    links: estate.links,
    mcpById: byId(estate.mcp),
    sourceById: byId(estate.sources),
    gatewayPaths: estate.gatewayPaths,
    own: emptyOwnState(),
  });
};
